'use client'

import React, {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {DebugPanelModules, DebugPanelProvider} from '@vio/debug-panel'
import {getPersistedSimulationFlag} from '@vio/debug-panel/src/utils/scenarioSimulator'
import {EmailSignUpProvider} from '@vio/shared/src/authentication/Email/EmailSignUpProvider'
import {EmailFlow} from '@vio/shared/src/authentication/Email/index'
import {useSUSIBottomSheet} from '@vio/shared/src/authentication/hooks/useSUSIBottomSheet'
import {OneTimePasswordFlow} from '@vio/shared/src/authentication/OneTimePassword/index'
import {PasswordlessLogin} from '@vio/shared/src/authentication/SUSI/overlay'
import {DealFreeze} from '@vio/shared/src/dealFreeze'
import {ApplicationNames} from '@vio/shared/src/dealFreeze/modules/slice'
import {SyncStateWithPriceWatchHoldoutGroup} from '@vio/shared/src/priceWatch/components/SyncStateWithPriceWatchHoldoutGroup'
import PerformanceMonitor from '@vio/shared/src/utils/PerformanceMonitor/PerformanceMonitor'
import {TmsPanelProvider} from '@vio/tms'
import {CaptureVioClickID} from 'components/CaptureVioClickID'
import SyncStateWithDeviceLayout from 'components/data/SyncStateWithDeviceLayout'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import InitializeSentry from 'components/InitializeSentry'
import {PreferencesOverlay} from 'components/PreferencesOverlay'
import {PriceWatchOverlays} from 'components/PriceWatch/components/PriceWatchOverlays'
import {PriceWatchTrackingsSync} from 'components/PriceWatch/components/PriceWatchTrackingsSync'
import InjectPageHeadTags from 'components/Seo/InjectPageHeadTags'
import InjectPageMetaDescription from 'components/Seo/InjectPageMetaDescription'
import {TrackGoogleTranslateUsage} from 'components/TrackGoogleTranslateUsage/TrackGoogleTranslateUsage'
import {TrackSSRExperiments} from 'components/TrackSSRExperiments/TrackSSRExperiments'
import {TrackUserAgentChange} from 'components/TrackUserAgentChange'
import {Web2AppPopup} from 'components/Web2AppPopup/Web2AppPopup'
import {usePageLoadStartTimer} from 'hooks/usePageLoad'
import {useSyncOverlayState} from 'hooks/useSyncOverlayState'
import {scenariosList} from 'mocks/scenarios'
import {setScreenshotTaken} from 'modules/analytics/slice'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceLayout,
  getIsSyntheticTest,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'

import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'
import {getQueryStringObject} from '@daedalus/core/src/_web/utils/url'
import {FetchAclDecision} from '@daedalus/core/src/acl/components/FetchAclDecision'
import InitializeDataDogClient from '@daedalus/core/src/analytics/components/DataDogClient'
import FullStoryLoader, {
  InjectFullStoryBodyClass
} from '@daedalus/core/src/analytics/components/FullStoryLoader'
import {InitializeCustomerIo} from '@daedalus/core/src/analytics/components/InitializeCustomerIo'
import TrackScreenshot from '@daedalus/core/src/analytics/components/TrackScreenshot'
import {TrackWebVitals} from '@daedalus/core/src/analytics/components/TrackWebVitals'
import {getPersistedVclidValue} from '@daedalus/core/src/analytics/utils/vclidCookie'
import {FetchUser} from '@daedalus/core/src/auth/components/FetchUser'
import SyncStateWithPrivilege from '@daedalus/core/src/auth/components/SyncStateWithPrivilege'
import TrackUserAuthentication from '@daedalus/core/src/auth/components/TrackUserAuthentication'
import {configureAmplify} from '@daedalus/core/src/auth/services'
import {
  checkIsBrandVio,
  getCheckoutHost,
  getMyBookingsHost
} from '@daedalus/core/src/brand/utils'
import {experimentVariationsStore} from '@daedalus/core/src/experiments/components/OpticksProvider'
import {getDataFile} from '@daedalus/core/src/experiments/modules/selectors'
import LanguageConfig from '@daedalus/core/src/localization/components/LanguageConfig'
import {GetBoVioConfiguration} from '@daedalus/core/src/offer/services/offerConfigurationApi/GetBoVioConfiguration'
import {urlHasParam} from '@daedalus/core/src/utils/url'
import {ProfileKey} from '@findhotel/sapi'

import {getSearchIdFromConditionalSource} from './modules/searchApi/selectors'
import {openSearchBox, setIsSearchBoxActive} from './modules/searchBox/slice'
import {imageProvider} from './utils/imageProvider'

configureAmplify()

/**
 * Global components or hooks to mount for all pages in the app.
 * This module is loaded client-side only (for now)
 *
 * This component should NOT render providers, go to AppServerProviders / AppProviders instead
 */
const AppGlobals = () => {
  usePageLoadStartTimer()
  useSUSIBottomSheet()
  useSyncOverlayState()

  const languageCode = useSelector(getLanguageCode)
  const {brand} = useBrand()
  const anonymousId = useSelector(getAnonymousId)
  const userCountryCode = useSelector(getUserCountryCode)
  const currencyCode = useSelector(getCurrencyCode)

  const appEnv = process.env.NEXT_PUBLIC_ENV
  const publicUrl = process.env.NEXT_PUBLIC_ASSETS_ENDPOINT
  const sapiProfileKey = process.env.NEXT_PUBLIC_SAPI_PROFILE_KEY as ProfileKey

  const boVioEndpoint = process.env.NEXT_PUBLIC_BOVIO_ENDPOINT
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION
  const checkoutUrl = getCheckoutHost({brand, appEnv})
  const bookingManagementUrl = getMyBookingsHost({
    brand,
    appEnv
  })

  const deviceLayout = useSelector(getDeviceLayout)
  const isMobile = deviceLayout !== 'desktop'
  const isSyntheticTest = useSelector(getIsSyntheticTest)
  const {updateLocation} = useUrlUpdater()

  const vclid = getQueryStringObject()?.vclid || getPersistedVclidValue()

  const oneTimePasswordUrl = `${process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}auth/send-one-time-password${checkIsBrandVio(brand) ? '' : '-etrip'}`

  const verifyUrl = `${process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}auth/verify-one-time-password${checkIsBrandVio(brand) ? '' : '-etrip'}`

  const datafile = useSelector(getDataFile)
  const optimizelyFlags = useMemo(
    () => datafile?.featureFlags?.map(({key}) => key) || [],
    [datafile]
  )

  useEffect(() => {
    if (
      urlHasParam({search: window.location.search, param: 'simulate'}) ||
      getPersistedSimulationFlag()
    ) {
      import('mocks/initializeSimulator').then(module => module.default())
    }
  }, [])

  return (
    <>
      <InitializeSentry />

      {/* Capture and store Vio Click Identifier */}
      <CaptureVioClickID />
      {/* Capture User Agent change */}
      <TrackUserAgentChange />
      <TrackScreenshot action={setScreenshotTaken} />
      <TrackGoogleTranslateUsage />
      <InitializeDataDogClient
        clientToken={process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN}
        appVersion={appVersion}
        appEnv={appEnv}
        applicationId={process.env.NEXT_PUBLIC_DD_RUM_APP_ID}
        sampleRate={Number(process.env.NEXT_PUBLIC_DD_SAMPLE_RATE)}
        premiumSampleRate={Number(
          process.env.NEXT_PUBLIC_DD_REPLAY_SAMPLE_RATE
        )}
        service="search"
      />

      <PerformanceMonitor />
      <TrackSSRExperiments />
      <TmsPanelProvider appEnv={appEnv} />

      <FullStoryLoader publicUrl={publicUrl} />
      <InjectFullStoryBodyClass />
      <InitializeCustomerIo />
      <SyncStateWithDeviceLayout />
      <LanguageConfig locale={languageCode} />

      <TrackWebVitals />
      <SyncStateWithPrivilege />
      <SyncStateWithPriceWatchHoldoutGroup />

      <FetchUser publicUrl={publicUrl} />
      <PriceWatchTrackingsSync />
      <FetchAclDecision anonymousId={anonymousId} />
      <TrackUserAuthentication />
      <InjectPageHeadTags />
      <InjectPageMetaDescription />

      <PriceWatchOverlays />
      <PreferencesOverlay />

      <PasswordlessLogin
        brand={brand}
        languageCode={languageCode}
        anonymousId={anonymousId}
      />
      <GetBoVioConfiguration
        boVioUrl={boVioEndpoint}
        brandCode={brand.code}
        appEnv={appEnv}
        urlParams={getQueryStringObject()}
      />
      <DealFreeze
        imageProvider={imageProvider}
        appCurrency={currencyCode}
        sapiProfileKey={sapiProfileKey}
        application={ApplicationNames.Search}
        appVersion={appVersion}
        appEnv={appEnv}
        boVioEndpoint={boVioEndpoint}
        optimizelyFlags={optimizelyFlags}
        checkoutUrl={checkoutUrl}
        bookingManagementUrl={bookingManagementUrl}
        languageCode={languageCode}
        actions={{
          openSearchBox,
          setIsSearchBoxActive
        }}
        getSearchId={getSearchIdFromConditionalSource}
        getCountryCode={getUserCountryCode}
        vclid={vclid ? String(vclid) : undefined}
      />
      <OneTimePasswordFlow
        brand={brand}
        languageCode={languageCode}
        oneTimePasswordUrl={oneTimePasswordUrl}
        verifyUrl={verifyUrl}
        userCountryCode={userCountryCode}
      />
      <Web2AppPopup />
      <EmailSignUpProvider
        languageCode={languageCode}
        apiUrl={process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}
      >
        <EmailFlow />
      </EmailSignUpProvider>

      <DebugPanelProvider
        isMobile={isMobile}
        appEnv={appEnv}
        isSyntheticTest={isSyntheticTest}
        thirdPartyExperiments={optimizelyFlags}
        meta={{anonymousId}}
        variationsStoreReader={experimentVariationsStore}
        scenarios={scenariosList}
        modulesConfig={[
          DebugPanelModules.EXPERIMENTS,
          DebugPanelModules.SIMULATOR,
          DebugPanelModules.SETTINGS,
          DebugPanelModules.SORT
        ]}
        updateLocation={updateLocation}
      />
    </>
  )
}

export default AppGlobals
